export type PickPlaceStage =
  | "hover"
  | "approach"
  | "final"
  | "close"
  | "lift"
  | "carry_hover"
  | "drop_approach"
  | "drop_open";

export type PickPlaceServoConfig = ReturnType<typeof createPickPlaceServoConfig>;

export function createPickPlaceServoConfig(useGripperTargetSite: boolean) {
  return {
    // Staged pick:
    // - hover: above the object
    // - approach: descend to a safe height over the target (avoid ground drag)
    // - final: descend to the target's origin (align target spheres) before closing
    // - close: close fingers (no attachment; pure contact grasp)
    // - lift: very small lift after closing
    // In Z-up chain frame, "down" is -Z.
    hoverHeight: 0.12,
    approachHeight: 0.03,
    // When using gripper_target as the IK point, the site is already intended to coincide
    // with the grasp center, so don't apply an extra downward bias in final stage.
    finalHeight: useGripperTargetSite ? 0.0 : -0.01,
    liftDelta: 0.02,
    // Pick/place: when moving to a dropoff target (e.g. tray on the ground),
    // we don't need the same hover clearance as the initial pick.
    // But we still want enough clearance to avoid dragging the object on the ground while carrying.
    carryHoverHeight: 0.12,
    dropApproachHeight: 0.06,
    // Lift height immediately after grasp, before translating toward dropoff.
    carryLiftHeight: 0.12,
    xyTolHover: 0.08,
    zTolHover: 0.06,
    rotTolHover: 0.25, // radians (~14°); gripper must be roughly pointing down before descending
    xyTolApproach: 0.012,
    zTolApproach: 0.012,
    rotTolApproach: 6.0e-2, // radians; require good orientation alignment before closing
    approachSettleSeconds: 0.18, // must stay in-tolerance continuously before descending to final
    finalSettleSeconds: 0.25, // must stay in-tolerance continuously at final height before closing
    finalRelSpeedTol: 0.15, // m/s; allow a bit of object jitter before closing
    closeHoldSeconds: 1.2,
    liftSettleSeconds: 0.2,
    // Keep this near floor-contact + epsilon so low-clearance grasps are not rejected.
    graspRetryLiftMinY: 0.0215, // scene Y (meters)
    graspRetryMaxEeCubeDist: 0.13, // scene meters: Panda gripper_target sits farther from finger pads than 2f85
    graspRetryMinLiftDelta: 0.0025, // require measurable lift from close-time baseline
    graspRetryDecisionDelaySeconds: 0.8, // allow extra time for slower tendon closure before miss checks
    graspRetryFailureWindowSeconds: 1.2, // require sustained failed-lift signal before reopening/retrying
    dropApproachSettleSeconds: 0.22,
    openHoldSeconds: 0.35,
    approachAnchorReacquireDistance: 0.015, // m; ignore tiny target motion during approach/final
    approachStageTimeoutSeconds: 4.0, // avoid deadlock: retry another grasp if approach stalls
    graspFinalDownBias: 0.006, // m (chain Z): nudge down in final/close for contact
    maxMissingTargetPoseTicks: 30,
    maxIkSolveFailuresBeforeRecovery: 12,
    intervalMs: 33, // ~30 Hz
  };
}
